import { Callback } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"

// The stand-in the ember minimap leaves behind when it is collapsed: a small pill in the top-right
// corner of the screen naming what is hiding there. It has to be conspicuous enough that a player who
// collapsed the map ten minutes ago still knows the map exists, and small enough that leaving it
// collapsed is a real choice rather than a trade of one piece of clutter for another -- hence a
// one-word label next to the glyph rather than an instruction.
//
// Styled off the map's own panel (dark translucent plate, thin light rim) so the two read as the same
// object in two states.
const PAD_X = 6
const PAD_Y = 4
const CORNER = 5
const LABEL = "MAP"
const GLYPH = "🗺"
const GLYPH_SIZE = 11
const GAP = 4

const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.59
const PANEL_ALPHA_HOVER = 0.82
const BORDER_ALPHA = 0.36
const BORDER_ALPHA_HOVER = 0.75
const LABEL_COLOR = 0xcfd8dc
const LABEL_COLOR_HOVER = 0xffffff

export class EmberMinimapChip extends Container {
    private plate = new Graphics()
    private glyph: Text
    private label: Text

    constructor(onClick: Callback) {
        super()

        // No tint on the emoji -- it carries its own colours.
        this.glyph = new TextRenderer().fontSize(GLYPH_SIZE).render(GLYPH) as Text
        this.label = new TextRenderer().size("tiny").render(LABEL) as Text

        this.glyph.x = PAD_X
        this.label.x = PAD_X + this.glyph.width + GAP
        // Both sit on a shared centre line, so the glyph and the text don't stagger.
        const h = Math.max(this.glyph.height, this.label.height)
        this.glyph.y = PAD_Y + (h - this.glyph.height) / 2
        this.label.y = PAD_Y + (h - this.label.height) / 2

        this.addChild(this.plate, this.glyph, this.label)
        this.draw(false)

        this.interactive = true
        this.cursor = "pointer"
        // An explicit hit area a couple of px proud of the plate: the chip is small, and it is the
        // only way back to the map.
        this.hitArea = new Rectangle(-3, -3, this.getWidth() + 6, this.getHeight() + 6)
        this.on("mouseover", () => this.draw(true))
        this.on("mouseout", () => this.draw(false))
        this.on("click", onClick)
        this.on("tap", onClick)

        this.visible = false
    }

    private draw = (hover: boolean) => {
        this.plate.clear()
        this.plate.beginFill(PANEL_COLOR, hover ? PANEL_ALPHA_HOVER : PANEL_ALPHA)
        this.plate.lineStyle(1, 0xffffff, hover ? BORDER_ALPHA_HOVER : BORDER_ALPHA)
        this.plate.drawRoundedRect(0, 0, this.getWidth(), this.getHeight(), CORNER)
        this.plate.endFill()
        this.label.tint = hover ? LABEL_COLOR_HOVER : LABEL_COLOR
    }

    // Measured off the content rather than getBounds(), so the plate can be drawn to the size the
    // caller is about to position against without a chicken-and-egg on bounds.
    getWidth = (): number => PAD_X * 2 + this.glyph.width + GAP + this.label.width
    getHeight = (): number => PAD_Y * 2 + Math.max(this.glyph.height, this.label.height)
}
